import {assert} from 'Asserts';
import {
  getStorableSchemaFromCache,
  getTagSchemaOrSchemaInterfaceFromCache,
} from 'lookup-config/AllSchemasQueries';
import {
  LookupStepSpec,
  LookupStepType,
  NotebookColumnItemType,
} from 'lookup-config/LookupConfigModalConstants';
import {NotebookColumnInfo} from 'notebook/structured-tables/lookup-columns/runLookupConfigModalForNotebook';
import {Schema} from 'schemas/SchemaConstants';
import APIUtil from 'util/APIUtil';

export const DEFAULT_ENTITY_SCHEMA_OPTION_NAME = 'Any entity';

export function getColumnInfoByProvenanceUuid(
  columnInfos: Array<NotebookColumnInfo>,
  inputProvenanceUuid: string | null
): NotebookColumnInfo | null {
  return columnInfos.find(({provenanceUuid}) => provenanceUuid === inputProvenanceUuid) ?? null;
}

export function getAnySchemaOptionName(notebookColumnType?: NotebookColumnItemType): string {
  switch (notebookColumnType) {
    case NotebookColumnItemType.CONTAINER:
      return 'Any container';
    case NotebookColumnItemType.BOX:
      return 'Any box';
    case NotebookColumnItemType.PLATE:
      return 'Any plate';
    case NotebookColumnItemType.LOCATION:
      return 'Any location';
    case NotebookColumnItemType.STORABLE:
      return 'Any inventory';
    case NotebookColumnItemType.ENTITY:
    default:
      return DEFAULT_ENTITY_SCHEMA_OPTION_NAME;
  }
}

/**
 * Helper to initialize inputColumnProvenanceUuid state in LookupConfigModal.
 *
 * If an initial input column info is provided and actually exists in the table, return its provenance UUID.
 * Otherwise, return the provenance UUID of the first valid source column (if one exists).
 */
export function getInitialInputColumnProvenanceUuid(
  notebookColumnInfos: Array<NotebookColumnInfo>,
  initialInputColumnInfo?: NotebookColumnInfo
): string {
  // If input column provenance UUID is specified (should only happen when editing an existing lookup column
  // config), then use that as the initial selected column.
  if (initialInputColumnInfo != null) {
    assert(
      notebookColumnInfos.findIndex(
        ({provenanceUuid}: NotebookColumnInfo) => provenanceUuid === initialInputColumnInfo.provenanceUuid
      ) !== -1,
      `Column with prov. UUID: ${initialInputColumnInfo.provenanceUuid} either does not exist or is not a
      valid input column for lookup.`
    );
    return initialInputColumnInfo.provenanceUuid;
  }
  // Otherwise, use the first column's provenance UUID if it exists.
  return notebookColumnInfos[0]?.provenanceUuid ?? null;
}

/**
 * Provided an input column, generate a default starting lookup step
 */
export function getDefaultLookupStepForInputColumn(
  columnInfos: Array<NotebookColumnInfo>,
  inputColumnProvenanceUuid: string | null
): {lookupStep: LookupStepSpec | null; additionalStepMetadata?: {storableLookupMustBeParent?: boolean}} {
  const selectedInputColumnInfo = getColumnInfoByProvenanceUuid(columnInfos, inputColumnProvenanceUuid);
  if (selectedInputColumnInfo == null) {
    return {lookupStep: null};
  }
  const requiredLinkSchemaId = selectedInputColumnInfo.schemaId ?? null;
  let requiredLink: Schema | null = null;
  if (requiredLinkSchemaId) {
    if (APIUtil.isStorableSchema(requiredLinkSchemaId)) {
      requiredLink = getStorableSchemaFromCache(requiredLinkSchemaId);
    } else {
      requiredLink = getTagSchemaOrSchemaInterfaceFromCache(requiredLinkSchemaId);
    }
  }
  let lookupStep: LookupStepSpec;
  if (requiredLink) {
    let notebookColumnSchemaProperties = {};
    if (APIUtil.isStorableSchema(requiredLinkSchemaId!)) {
      notebookColumnSchemaProperties = {notebookColumnStorableSchema: requiredLinkSchemaId!};
    } else {
      notebookColumnSchemaProperties = {notebookColumnEntitySchema: requiredLinkSchemaId!};
    }
    lookupStep = {
      type: LookupStepType.NOTEBOOK_COLUMN_TYPECHECK,
      notebookColumnItemType: selectedInputColumnInfo.itemType,
      ...notebookColumnSchemaProperties,
    };
  } else {
    const notebookColumnItemType = selectedInputColumnInfo.itemType ?? NotebookColumnItemType.ENTITY;
    lookupStep = {
      type: LookupStepType.NOTEBOOK_COLUMN_TYPECHECK,
      notebookColumnItemType,
    };
  }
  return {
    lookupStep,
    additionalStepMetadata: {
      ...(selectedInputColumnInfo.storableLookupMustBeParent != null && {
        storableLookupMustBeParent: selectedInputColumnInfo.storableLookupMustBeParent,
      }),
    },
  };
}
